import { Switch } from "@/components/shadcn/switch";
import { Button } from "@/components/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/shadcn/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { AddNewEntry } from "./AssistantDetailsContent";
import { NoxusMenu } from "../UI/NoxusMenu";
import { getIcon } from "@/utils/assistants";
import { Bot } from "lucide-react";
import {
  ChevronDown,
  BookOpen,
  Lightbulb,
  Trash,
  X,
  Check,
  Plus,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ToolAssociationConfig {
  tool_names: string[];
  mode: "PREFER" | "REQUIRE" | "BLOCK";
}

export interface GuidelineConfig {
  id: string;
  condition: string;
  action: string;
  priority?: string;
  tags?: string[];
  enabled?: boolean;
  tool_association?: ToolAssociationConfig;
  tool_associations?: ToolAssociationConfig[];
}

export interface GlossaryTermConfig {
  term: string;
  definition: string;
  synonyms?: string[];
}

interface AvailableTool {
  type: string;
  title?: string;
  description?: string;
  icon?: string;
  bg_color?: string;
  icon_color?: string;
  enabled?: boolean;
}

interface AvailableMcpServer {
  type: string;
  title?: string;
  image?: string;
  enabled?: boolean;
}

interface ToolOption {
  key: string;
  title: string;
  description?: string;
  /** Lucide icon name for regular tools */
  icon?: string;
  bgColor?: string;
  iconColor?: string;
  /** Raw image string for MCP servers ("icon:Name" or URL) */
  image?: string;
  patterns: string[];
}

// ---------------------------------------------------------------------------
// Static tool → patterns mapping (no filesystem)
// ---------------------------------------------------------------------------

const TOOL_PATTERNS_BY_TYPE: Record<string, string[]> = {
  web_research: ["web_research", "fetch_url"],
  human_in_the_loop: ["human_in_the_loop"],
  memory: ["reset_memory"],
  todos: ["todos_*"],
  sandbox: ["sandbox_*"],
  schedule_tool: [
    "create_periodic_task",
    "create_call_later_task",
    "list_periodic_tasks",
    "delete_periodic_task",
  ],
  kb_selector: ["kb_selector_qa_*"],
  kb_qa: ["kb_qa_*"],
  noxus_qa: ["kb_qa_*"],
  workflow: ["execute_*"],
  agent_tool: ["agent_*"],
  action: ["execute_*"],
};

function buildToolOptions(
  availableTools: AvailableTool[],
  availableMcpServers: AvailableMcpServer[]
): ToolOption[] {
  const seenPatternsKey = new Set<string>();
  const options: ToolOption[] = [];

  for (const tool of availableTools) {
    if (tool.enabled === false) continue;
    if (!tool.type) continue;
    const patterns = TOOL_PATTERNS_BY_TYPE[tool.type];
    if (!patterns) continue;

    const patternsKey = patterns.join(",");
    if (seenPatternsKey.has(patternsKey)) continue;
    seenPatternsKey.add(patternsKey);

    options.push({
      key: `tool_${tool.type}`,
      title: tool.title || tool.type,
      description: tool.description,
      icon: tool.icon,
      bgColor: tool.bg_color,
      iconColor: tool.icon_color,
      patterns,
    });
  }

  for (const server of availableMcpServers) {
    if (server.enabled === false) continue;
    if (!server.type) continue;
    options.push({
      key: `mcp_${server.type}`,
      title: server.title || server.type,
      image: server.image,
      patterns: [`mcp__${server.type}__*`],
    });
  }

  return options;
}

function isToolSelected(option: ToolOption, toolNames: string[]): boolean {
  return option.patterns.some((p) => toolNames.includes(p));
}

function toggleTool(
  option: ToolOption,
  toolNames: string[],
  selected: boolean
): string[] {
  if (selected) {
    const newNames = new Set(toolNames);
    for (const p of option.patterns) newNames.add(p);
    return Array.from(newNames);
  } else {
    return toolNames.filter((n) => !option.patterns.includes(n));
  }
}

// ---------------------------------------------------------------------------
// ToolOptionIcon — colored icon pill matching QuickActionEntry styling
// ---------------------------------------------------------------------------

const ToolOptionIcon = ({
  opt,
  size = "md",
}: {
  opt: ToolOption;
  size?: "sm" | "md";
}) => {
  const padding = size === "sm" ? "p-1" : "p-1.5";
  const iconSize = size === "sm" ? 12 : 14;

  if (opt.image) {
    // MCP server with image reference — use a neutral Bot icon
    return (
      <div
        className={cn(
          "flex rounded-md justify-center items-center shrink-0",
          padding
        )}
        style={{ backgroundColor: "#f3f4f6", color: "#6b7280" }}
      >
        <Bot width={iconSize} height={iconSize} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex rounded-md justify-center items-center shrink-0",
        padding
      )}
      style={{
        backgroundColor: opt.bgColor || "#eff6ff",
        color: opt.iconColor || "#3b82f6",
      }}
    >
      {getIcon(opt.icon)}
    </div>
  );
};

const PRIORITY_OPTIONS = [
  { value: "CRITICAL", label: "Critical" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
  { value: "FALLBACK", label: "Fallback" },
];

const EMPTY_GUIDELINE = (): GuidelineConfig => ({
  id: crypto.randomUUID(),
  condition: "",
  action: "",
  priority: "NORMAL",
  tags: [],
  enabled: true,
});

const EMPTY_GLOSSARY_TERM = (): GlossaryTermConfig => ({
  term: "",
  definition: "",
  synonyms: [],
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface GuidelinesFieldProps {
  guidelines: GuidelineConfig[] | undefined;
  availableTools?: AvailableTool[];
  availableMcpServers?: AvailableMcpServer[];
  onGuidelinesChange: (guidelines: GuidelineConfig[]) => void;
  isDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const GuidelinesField = ({
  guidelines,
  availableTools,
  availableMcpServers,
  onGuidelinesChange,
  isDisabled,
}: GuidelinesFieldProps) => {
  const currentGuidelines = useMemo(() => guidelines || [], [guidelines]);

  const updateGuideline = useCallback(
    (index: number, patch: Partial<GuidelineConfig>) => {
      if (isDisabled) return;
      onGuidelinesChange(
        currentGuidelines.map((g, i) => (i === index ? { ...g, ...patch } : g))
      );
    },
    [currentGuidelines, onGuidelinesChange, isDisabled]
  );

  const deleteGuideline = useCallback(
    (index: number) => {
      if (isDisabled) return;
      onGuidelinesChange(currentGuidelines.filter((_, i) => i !== index));
    },
    [currentGuidelines, onGuidelinesChange, isDisabled]
  );

  const addGuideline = useCallback(() => {
    if (isDisabled) return;
    onGuidelinesChange([...currentGuidelines, EMPTY_GUIDELINE()]);
  }, [currentGuidelines, onGuidelinesChange, isDisabled]);

  return (
    <div className="flex flex-col gap-4">
      {/* Guidelines list */}
      <div className="flex flex-col gap-2">
        {currentGuidelines.map((guideline, index) => (
          <GuidelineCard
            key={guideline.id}
            guideline={guideline}
            availableTools={availableTools}
            availableMcpServers={availableMcpServers}
            onToggle={(enabled) => updateGuideline(index, { enabled })}
            onChange={(patch) => updateGuideline(index, patch)}
            onDelete={() => deleteGuideline(index)}
            isDisabled={isDisabled}
          />
        ))}
        <AddNewEntry
          label="Add Guideline"
          onClick={addGuideline}
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GuidelineCard — collapsible card for a single When/Then rule
// ---------------------------------------------------------------------------

const TOOL_MODE_OPTIONS: {
  value: "PREFER" | "REQUIRE" | "BLOCK";
  label: string;
  title: string;
}[] = [
  {
    value: "PREFER",
    label: "Prefer",
    title: "Suggest the LLM uses these tools",
  },
  {
    value: "REQUIRE",
    label: "Require",
    title: "Force the LLM to call these tools",
  },
  {
    value: "BLOCK",
    label: "Block",
    title: "Remove these tools from the LLM's options",
  },
];

const GuidelineCard = ({
  guideline,
  availableTools,
  availableMcpServers,
  onToggle,
  onChange,
  onDelete,
  isDisabled,
}: {
  guideline: GuidelineConfig;
  availableTools?: AvailableTool[];
  availableMcpServers?: AvailableMcpServer[];
  onToggle: (enabled: boolean) => void;
  onChange: (patch: Partial<GuidelineConfig>) => void;
  onDelete: () => void;
  isDisabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(!guideline.condition);

  const associations = useMemo(
    () => guideline.tool_associations ?? [],
    [guideline.tool_associations]
  );

  const conditionPreview = guideline.condition
    ? `When ${guideline.condition}`
    : "New Guideline";

  const toolOptions = useMemo(
    () => buildToolOptions(availableTools ?? [], availableMcpServers ?? []),
    [availableTools, availableMcpServers]
  );

  const updateAssociation = useCallback(
    (index: number, patch: Partial<ToolAssociationConfig>) => {
      const updated = associations.map((a, i) =>
        i === index ? { ...a, ...patch } : a
      );
      onChange({ tool_associations: updated });
    },
    [associations, onChange]
  );

  const deleteAssociation = useCallback(
    (index: number) => {
      onChange({
        tool_associations: associations.filter((_, i) => i !== index),
      });
    },
    [associations, onChange]
  );

  const addAssociation = useCallback(() => {
    onChange({
      tool_associations: [...associations, { tool_names: [], mode: "PREFER" }],
    });
  }, [associations, onChange]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn("border border-border rounded-md w-full overflow-hidden")}
      >
        {/* Header row */}
        <div className="flex items-center justify-between gap-2 p-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
            >
              <div className="flex bg-accent text-muted-foreground p-1.5 rounded-md justify-center items-center shrink-0">
                <Lightbulb width={14} height={14} />
              </div>
              <div className="flex flex-col min-w-0 text-left">
                <p className="font-sm text-sm truncate">{conditionPreview}</p>
                {guideline.action && (
                  <p className="text-xs text-muted-foreground truncate">
                    Then {guideline.action}
                  </p>
                )}
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </button>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              checked={guideline.enabled !== false}
              onCheckedChange={onToggle}
              disabled={isDisabled}
            />
            <NoxusMenu
              itemsArray={[
                {
                  label: "Delete",
                  icon: Trash,
                  onClick: onDelete,
                  color: "destructive",
                },
              ]}
              isDisabled={isDisabled}
            />
          </div>
        </div>

        {/* Expanded content */}
        <CollapsibleContent>
          <div className="border-t border-border p-3 flex flex-col gap-3">
            {/* When condition */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                When…
              </label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 text-sm border border-input rounded-md resize-y bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. the user asks about pricing"
                value={guideline.condition}
                onChange={(e) => onChange({ condition: e.target.value })}
                disabled={isDisabled}
              />
            </div>

            {/* Then action */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Then…
              </label>
              <textarea
                className="w-full min-h-[60px] px-3 py-2 text-sm border border-input rounded-md resize-y bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. always mention the free tier"
                value={guideline.action}
                onChange={(e) => onChange({ action: e.target.value })}
                disabled={isDisabled}
              />
            </div>

            {/* Priority */}
            <div className="flex flex-col gap-1.5 w-40">
              <label className="text-xs font-medium text-muted-foreground">
                Priority
              </label>
              <Select
                value={guideline.priority || "NORMAL"}
                onValueChange={(value) => onChange({ priority: value })}
                disabled={isDisabled}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tool Enforcement Rules */}
            {associations.map((assoc, assocIdx) => (
              <ToolEnforcementRow
                key={assocIdx}
                association={assoc}
                toolOptions={toolOptions}
                onChange={(patch) => updateAssociation(assocIdx, patch)}
                onDelete={() => deleteAssociation(assocIdx)}
                isDisabled={isDisabled}
              />
            ))}
            <button
              type="button"
              className="self-start text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
              onClick={addAssociation}
              disabled={isDisabled}
            >
              <Plus className="h-3 w-3" />
              Add tool enforcement
            </button>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// ToolEnforcementRow — a single mode + tool set
// ---------------------------------------------------------------------------

const ToolEnforcementRow = ({
  association,
  toolOptions,
  onChange,
  onDelete,
  isDisabled,
}: {
  association: ToolAssociationConfig;
  toolOptions: ToolOption[];
  onChange: (patch: Partial<ToolAssociationConfig>) => void;
  onDelete: () => void;
  isDisabled?: boolean;
}) => {
  const currentToolNames = association.tool_names;

  const selectedChips = useMemo(
    () => toolOptions.filter((opt) => isToolSelected(opt, currentToolNames)),
    [toolOptions, currentToolNames]
  );

  const handleToolToggle = useCallback(
    (opt: ToolOption, selected: boolean) => {
      const newNames = toggleTool(opt, currentToolNames, selected);
      onChange({ tool_names: newNames });
    },
    [currentToolNames, onChange]
  );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-input p-2.5">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {TOOL_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.title}
              className={cn(
                "px-2.5 py-1 text-xs rounded-md border transition-colors",
                association.mode === opt.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-input text-muted-foreground hover:text-foreground"
              )}
              onClick={() => onChange({ mode: opt.value })}
              disabled={isDisabled}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-destructive transition-colors"
          onClick={onDelete}
          disabled={isDisabled}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tool picker popover */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full px-3 py-1.5 text-sm border border-input rounded-md bg-background text-left hover:bg-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isDisabled}
          >
            <span className="text-muted-foreground">Select tools…</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          {toolOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-1.5">
              No tools enabled. Enable tools in the Actions tab first.
            </p>
          ) : (
            <div className="flex flex-col max-h-56 overflow-y-auto">
              {toolOptions.map((opt) => {
                const selected = isToolSelected(opt, currentToolNames);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={cn(
                      "flex items-center gap-2.5 px-2 py-2 rounded-md transition-colors w-full text-left",
                      selected ? "bg-accent/70" : "hover:bg-accent/50"
                    )}
                    onClick={() => handleToolToggle(opt, !selected)}
                    disabled={isDisabled}
                  >
                    <ToolOptionIcon opt={opt} />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm font-medium leading-none truncate">
                        {opt.title}
                      </span>
                      {opt.description && (
                        <span className="text-xs text-muted-foreground mt-0.5 truncate">
                          {opt.description}
                        </span>
                      )}
                    </div>
                    {selected && (
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Selected tool chips */}
      {selectedChips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedChips.map((chip) => (
            <div
              key={chip.key}
              className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-accent border border-border"
            >
              <ToolOptionIcon opt={chip} size="sm" />
              <span className="text-xs">{chip.title}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive transition-colors"
                onClick={() => handleToolToggle(chip, false)}
                disabled={isDisabled}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// GlossaryTermRow — inline row for a single glossary term
// ---------------------------------------------------------------------------

const GlossaryTermRow = ({
  term,
  onChange,
  onDelete,
  isDisabled,
}: {
  term: GlossaryTermConfig;
  onChange: (patch: Partial<GlossaryTermConfig>) => void;
  onDelete: () => void;
  isDisabled?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(!term.term);

  return (
    <div className="border border-border rounded-md overflow-hidden">
      {/* Collapsed header */}
      <div className="flex items-center gap-2 px-2.5 py-2">
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer text-left"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="text-sm truncate">
            {term.term || (
              <span className="text-muted-foreground italic">New term</span>
            )}
          </span>
          {term.definition && !isExpanded && (
            <span className="text-xs text-muted-foreground truncate">
              - {term.definition}
            </span>
          )}
        </button>
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-destructive transition-colors shrink-0"
          onClick={onDelete}
          disabled={isDisabled}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expanded fields */}
      {isExpanded && (
        <div className="border-t border-border p-2.5 flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs text-muted-foreground">Term</label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="e.g. NRR"
                value={term.term}
                onChange={(e) => onChange({ term: e.target.value })}
                disabled={isDisabled}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Definition</label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Net Revenue Retention"
              value={term.definition}
              onChange={(e) => onChange({ definition: e.target.value })}
              disabled={isDisabled}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">
              Synonyms (comma-separated)
            </label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-input rounded-md bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g. Net Retention, Net Dollar Retention"
              value={(term.synonyms || []).join(", ")}
              onChange={(e) =>
                onChange({
                  synonyms: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              disabled={isDisabled}
            />
          </div>
          <button
            type="button"
            className="self-end text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
            onClick={() => setIsExpanded(false)}
          >
            <Check className="h-3 w-3" />
            Done
          </button>
        </div>
      )}
    </div>
  );
};
